import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Every direct `window`/`document` access in the public tree lives in this file and
 * `hooks.ts`, and nowhere else.
 *
 * §1.10 forbids module-scope browser globals because they break SSR, and ESLint enforces
 * that with `no-restricted-globals` across `apps/web`. Rather than scatter disable
 * comments through the components, the access is concentrated here — where each use is
 * inside an effect, reviewed once, and shared by every component that needs it. That also
 * removed the duplicate focus-trap and scroll-lock implementations the dialog and the
 * navigation drawer were each carrying.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function scrollToTop(): void {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

export function printPage(): void {
  window.print();
}

/**
 * The `/` shortcut that opens search (Phase 10 task list).
 *
 * Ignored while the reader is typing — in an input, a textarea, a select or anything
 * `contenteditable` — because a search shortcut that swallows the slash in a URL someone
 * is pasting is worse than no shortcut. Modifier combinations are left to the browser.
 */
export function useSearchShortcut(onOpen: () => void): void {
  const handler = useRef(onOpen);
  handler.current = onOpen;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return;
      }
      event.preventDefault();
      handler.current();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
}

/**
 * Restores scroll position on back navigation (Phase 10 task list).
 *
 * React Router does not do this, and the browser's own restoration cannot: the page is
 * re-rendered from cache after the router has already scrolled to the top. So each
 * location's offset is recorded as the reader leaves it and reapplied on a POP —
 * which is what makes "open a story from halfway down a category page and come back"
 * land where they left, rather than at the top of a list they have to scroll again.
 *
 * `sessionStorage`, not memory: a reload in the middle of a session should not lose it.
 */
const SCROLL_KEY = 'nk:scroll';

function readPositions(): Record<string, number> {
  try {
    return JSON.parse(sessionStorage.getItem(SCROLL_KEY) ?? '{}') as Record<string, number>;
  } catch {
    return {};
  }
}

/** Anything the reader does that means "I am steering now". */
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown'] as const;

export function useScrollRestoration(key: string, navigationType: string): void {
  useEffect(() => {
    // The browser keeps its own idea of where each history entry was scrolled to, and it
    // applies it on a back navigation *after* this effect runs — with the document at
    // whatever height it had reached mid-restore, which is shorter than the finished
    // page. Two restorers means the worse one wins, so the app takes the job outright.
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    const positions = readPositions();

    // Both branches wait a frame. On a POP the document has to be tall enough to accept
    // the offset; on a PUSH the browser's scroll anchoring can otherwise drag the new
    // page back down as images and embeds settle, which lands a reader halfway into a
    // story they just opened.
    const target = navigationType === 'POP' ? (positions[key] ?? 0) : 0;

    // One frame is not always enough. Coming back to a listing, the shell paints before
    // the feed does, and a document that is still 300px tall silently clamps the offset
    // — the reader asked for the story they were reading and lands at the top instead.
    // So the offset is re-applied until the page is tall enough to hold it, for at most
    // five seconds, and abandoned the moment the reader scrolls for themselves. The
    // budget has to cover a loaded phone refetching a listing, not just a warm laptop —
    // two seconds was enough here until the suite started running the CMS specs
    // alongside, and a reader on a slow connection is in exactly that position.
    let frame = 0;
    const deadline = Date.now() + 10_000;
    const settle = () => {
      // `instant`, explicitly: the global `scroll-behavior: smooth` is for in-page
      // anchors, and animating a 900px jump on every navigation is both slow and
      // disorienting — the reader watches the previous page scroll past on the way.
      window.scrollTo({ top: target, behavior: 'instant' });
      if (window.scrollY < target && Date.now() < deadline) frame = requestAnimationFrame(settle);
      else stop();
    };

    const stop = () => {
      cancelAnimationFrame(frame);
      for (const event of USER_SCROLL_EVENTS) window.removeEventListener(event, stop);
    };
    for (const event of USER_SCROLL_EVENTS) window.addEventListener(event, stop, { passive: true });
    frame = requestAnimationFrame(settle);

    // The position is tracked as the reader scrolls rather than read when leaving.
    // Reading it on the way out gives the wrong answer: by then the next route's DOM is
    // committed, the document is a different height, and the browser has already
    // clamped `scrollY` to fit it — 900px down a listing became 254 on the way to the
    // story, and coming back landed there. Found by the Phase 10 back-button test.
    let position = target;
    const track = () => {
      position = window.scrollY;
    };
    window.addEventListener('scroll', track, { passive: true });

    const save = () => {
      try {
        sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ ...readPositions(), [key]: position }));
      } catch {
        // Storage blocked: the reader loses scroll restoration, not the page.
      }
    };

    window.addEventListener('pagehide', save);
    return () => {
      stop();
      save();
      window.removeEventListener('scroll', track);
      window.removeEventListener('pagehide', save);
    };
  }, [key, navigationType]);
}

/**
 * The site's own origin, set once per process from `APP_ORIGIN` on the server and from
 * the document on the client.
 *
 * Module state is safe here in a way it usually is not during SSR: the origin is a
 * deployment constant, identical for every concurrent request, not per-reader state.
 *
 * It exists because the alternative does not work. Returning a *relative* URL during SSR
 * and an absolute one after mount produces markup that React does not correct: hydration
 * keeps the server's attribute, so every share link on a server-rendered page would stay
 * `?u=/en/state/story` — which shares nothing. Found by the Phase 9 share test.
 */
let origin = '';

export function setOrigin(value: string): void {
  origin = value.replace(/\/+$/, '');
}

/** Absolute URL for a path — the same string on the server and in the browser. */
export function absoluteUrl(path: string): string {
  if (origin) return `${origin}${path}`;
  if (typeof window !== 'undefined') return `${window.location.origin}${path}`;
  return path;
}

/**
 * Builds the real embed player for a facade a reader has just clicked (Phase 9).
 *
 * `autoplay=1` is added because the click *was* the request to play; anything else means
 * a second click for no reason.
 */
export function createEmbedIframe(src: string, title: string): HTMLIFrameElement {
  const separator = src.includes('?') ? '&' : '?';
  const frame = document.createElement('iframe');
  frame.src = `${src}${separator}autoplay=1`;
  frame.title = title;
  frame.className = 'absolute inset-0 h-full w-full border-0';
  frame.allow =
    'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
  frame.allowFullscreen = true;
  return frame;
}

/**
 * The portal target, available only after mount. Returning `null` on the server (and on
 * the first client render) is what keeps a dialog out of the server-rendered HTML, where
 * it has nothing to contribute and would hydrate into a mismatch.
 */
export function usePortalTarget(): HTMLElement | null {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  useEffect(() => setTarget(document.body), []);
  return target;
}

/** Stops the page behind an overlay from scrolling, and restores whatever was there. */
export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [active]);
}

/** Escape closes an overlay. Registered on the document so focus position is irrelevant. */
export function useEscapeKey(active: boolean, onEscape: () => void): void {
  const handler = useRef(onEscape);
  handler.current = onEscape;

  useEffect(() => {
    if (!active) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handler.current();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [active]);
}

/**
 * Traps Tab inside `container` while `active`, moves focus into it on open, and returns
 * focus to whatever was focused before on close.
 *
 * The third part is the one usually missed: without it, closing a dialog drops focus onto
 * `<body>` and a keyboard reader starts again from the top of the page.
 */
export function useFocusTrap(container: RefObject<HTMLElement>, active: boolean): void {
  useEffect(() => {
    if (!active) return undefined;

    const restoreTo = document.activeElement as HTMLElement | null;
    /*
     * `data-autofocus` wins over document order. Without it, opening the search overlay
     * puts focus on its close button — inside the trap, and useless: the reader has to
     * Tab to the field they opened the dialog to type in.
     *
     * Not `[autofocus]`: React's `autoFocus` prop does not render the attribute, it
     * calls `.focus()` itself on mount — and this effect then runs afterwards and takes
     * the focus away again. An explicit data attribute is the only version of this that
     * both sides agree on.
     */
    const panel = container.current;
    const preferred =
      panel?.querySelector<HTMLElement>('[data-autofocus]') ??
      panel?.querySelector<HTMLElement>(FOCUSABLE);
    preferred?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !container.current) return;
      const focusable = Array.from(container.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;

      // Wrap at both ends, so Tab can never reach the page behind the overlay.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      restoreTo?.focus();
    };
  }, [container, active]);
}

/**
 * `true` while the tab is in the background — the condition PRD §5's tab-title alert and
 * the live timeline's "new updates" pill both turn on.
 *
 * Starts `false` on the server and on the first client render: a page cannot know it is
 * hidden until the browser says so, and guessing would make the server's HTML disagree
 * with the hydrated output.
 */
export function usePageHidden(): boolean {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const update = () => setHidden(document.visibilityState === 'hidden');
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  return hidden;
}

/**
 * The tab-title alert (Phase 11): `NK NEWS TIME` becomes `(2) NK NEWS TIME` while the
 * reader is in another tab and breaking news arrives.
 *
 * The document's own title is captured once and restored on cleanup, so the count never
 * compounds — prefixing a title that already carries a prefix is how `(1) (1) (2) …`
 * happens. Nothing at all is done while the count is zero, which keeps a page that never
 * sees breaking news byte-identical to one without this hook.
 */
export function useTitleAlert(count: number): void {
  useEffect(() => {
    if (count <= 0) return undefined;

    const original = document.title;
    document.title = `(${count}) ${original}`;
    return () => {
      document.title = original;
    };
  }, [count]);
}

export function setDocumentLang(lang: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lang;
}

export function setCookie(name: string, value: string, maxAgeSeconds = 31536000): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${value}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
}
